/**
 * AUTH CONTEXT
 *
 * Centralized authentication state management for the React application.
 * Replaces server-side auth functions with client-side context.
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import type { User } from '@supabase/supabase-js';
import type { UserRole } from '@/types/database';
import { createClient } from '@/lib/supabase';

// Use the shared Supabase client instance
const supabase = createClient();

export interface AuthUser {
  id: string;
  email: string;
  role: UserRole;
  full_name: string | null;
  avatar_url: string | null;
  is_active: boolean;
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, fullName: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  /**
   * Fetch user profile from database
   */
  const fetchProfile = async (authUser: User): Promise<AuthUser | null> => {
    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', authUser.id)
        .single();

      if (error || !profile) {
        console.error('Error fetching profile:', error);
        return null;
      }

      return {
        id: profile.id,
        email: profile.email,
        role: profile.role,
        full_name: profile.full_name,
        avatar_url: profile.avatar_url,
        is_active: profile.is_active,
      };
    } catch (err) {
      console.error('Error fetching profile:', err);
      return null;
    }
  };


  /**
   * Initialize auth state and set up listener
   */
  useEffect(() => {
    let mounted = true;

    // Aggressive timeout to ALWAYS stop loading after 1 second (matching access portal)
    const forceStopLoading = setTimeout(() => {
      if (mounted) {
        console.warn('Auth initialization timeout - setting loading to false');
        setLoading(false);
      }
    }, 1000);

    // Listen for auth changes first
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;

      clearTimeout(forceStopLoading);

      if (session?.user) {
        // Fetch profile and wait for it before setting loading to false
        fetchProfile(session.user).then((profile) => {
          if (mounted) {
            setUser(profile);
            setLoading(false);
          }
        }).catch((err) => {
          console.error('Background profile fetch failed:', err);
          if (mounted) {
            setUser(null);
            setLoading(false);
          }
        });
      } else {
        setUser(null);
        setLoading(false);
      }
    });

    // Get initial session (but don't block on it)
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (!mounted) return;

      if (error) {
        console.error('Error getting session:', error);
        clearTimeout(forceStopLoading);
        setLoading(false);
        return;
      }

      // If no session immediately, stop loading
      if (!session) {
        console.warn('No initial session found');
        clearTimeout(forceStopLoading);
        setLoading(false);
      }
      // If session exists, the auth state change listener will handle it
    }).catch((err) => {
      console.error('Error in getSession:', err);
      if (mounted) {
        clearTimeout(forceStopLoading);
        setLoading(false);
      }
    });

    return () => {
      mounted = false;
      clearTimeout(forceStopLoading);
      subscription.unsubscribe();
    };
  }, []);

  /**
   * Sign in with email and password
   */
  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      throw error;
    }

    // Profile will be fetched by auth state change listener
  };

  /**
   * Sign up with email, password, and full name
   */
  const signUp = async (email: string, password: string, fullName: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
        },
      },
    });

    if (error) {
      throw error;
    }

    // Profile will be created by database trigger and fetched by auth state change listener
  };

  /**
   * Sign out
   */
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();

    // If session is already missing, that's fine - just clear the user
    if (error && error.message !== 'Auth session missing!') {
      throw error;
    }

    // Clear only auth-related state, not all localStorage
    // This preserves app data while clearing authentication
    setUser(null);
  };

  /**
   * Refresh user profile from database
   */
  const refreshUser = async () => {
    const {
      data: { user: authUser },
    } = await supabase.auth.getUser();

    if (authUser) {
      const profile = await fetchProfile(authUser);
      setUser(profile);
    }
  };

  const value: AuthContextValue = {
    user,
    loading,
    signIn,
    signUp,
    signOut,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access auth context
 * Must be used within AuthProvider
 */
export function useAuth() {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
